import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { HelpCircle, PlayCircle, Sparkles } from 'lucide-react';
import { useRouteHelp, type RouteHelp } from '@/hooks/use-route-help';
import { cn } from '@/lib/utils';
import { BAR_ICON_BUTTON_CLASS } from '@/components/layout/bar-styles';

// Botón "?" del Navbar: abre dropdown con secciones de ayuda registradas
// para la ruta actual + item fijo "Repetir tour de bienvenida".
//
// Las secciones se cargan desde `TOUR_SECTIONS` (config/tour-steps.ts) y
// matchean por `location.pathname` exacto.

export const HelpButton = () => {
  const help = useRouteHelp();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Ayuda"
          data-tour="navbar-help"
          className={BAR_ICON_BUTTON_CLASS}
        >
          <HelpCircle className="h-5 w-5" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(
            'border-border bg-surface text-text z-50 min-w-60 overflow-hidden rounded-md border p-1 shadow-md',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          <DropdownMenu.Label className="text-text-muted px-3 py-2 text-[10px] font-semibold tracking-wide uppercase">
            Ayuda contextual
          </DropdownMenu.Label>

          <HelpMenuItems help={help} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

interface HelpMenuItemsProps {
  help: RouteHelp;
}

// Opciones de ayuda de la página actual: tour completo, secciones sueltas y
// "Repetir tour de bienvenida". Las usan este dropdown y el submenú "Ayuda de
// esta página" del menú de usuario en el celular.
export const HelpMenuItems = ({ help }: HelpMenuItemsProps) => {
  return (
    <>
      {help.sections.length === 0 ? (
        <p className="text-text-muted px-3 pb-2 text-xs">Sin ayuda registrada en esta página.</p>
      ) : (
        <>
          <DropdownMenu.Item
            onSelect={help.runFullTour}
            className="data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm font-medium outline-hidden transition-colors select-none"
          >
            <PlayCircle className="text-primary h-4 w-4" aria-hidden="true" />
            Tour completo de esta página
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="bg-border my-1 h-px" />
          <DropdownMenu.Label className="text-text-muted px-3 py-1 text-[10px] font-semibold tracking-wide uppercase">
            Por sección
          </DropdownMenu.Label>
          {help.sections.map((section) => (
            <DropdownMenu.Item
              key={section.id}
              onSelect={() => help.runSection(section.id)}
              className="data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none"
            >
              {section.label}
            </DropdownMenu.Item>
          ))}
        </>
      )}

      <DropdownMenu.Separator className="bg-border my-1 h-px" />
      <DropdownMenu.Item
        onSelect={help.runOnboarding}
        className="data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none"
      >
        <Sparkles className="text-secondary h-4 w-4" aria-hidden="true" />
        Repetir tour de bienvenida
      </DropdownMenu.Item>
    </>
  );
};
